import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../../services/dataService';
import {
  SocioEconomicFields,
  emptySocioEconomic,
  isSocioEconomicComplete,
  socioEconomicFromProfile
} from '../components/SocioEconomicFields';
import { SocioEconomicData } from '../../types/domain';
import {
  getFamilyIncomeLabel,
  getFinancingTypeLabel,
  getParentEducationLabel
} from '../../lib/domain-data';

/**
 * FR-03 — the patient's own view of the socio-economic data (SoW Bagian 2.3).
 *
 * The SoW calls this "data tetap", filled once during onboarding, but also
 * says it can be edited if needed; this screen is the "if needed" half. It
 * exists as its own route because FR-02's profile editor does not yet, and is
 * built around the shared SocioEconomicFields so it collapses into that editor
 * without a rewrite when FR-02 lands.
 */
export default function FamilyDataPage() {
  const { profile, user, refreshProfile } = useAuth();

  const navigate = useNavigate();

  const stored = useMemo(() => socioEconomicFromProfile(profile), [profile]);
  const [draft, setDraft] = useState<SocioEconomicData>(emptySocioEconomic);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The profile arrives asynchronously and is refreshed after a save, so the
  // draft follows it rather than being seeded once from an initial value that
  // was still null on first render.
  useEffect(() => {
    setDraft(stored);
  }, [stored]);

  const handleSave = async () => {
    if (!user) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await dataService.updateSocioEconomic(user.id, draft);
      await refreshProfile();
      setNotice('Data keluarga tersimpan.');
    } catch (err: any) {
      setError(err?.message || 'Gagal menyimpan data keluarga.');
    } finally {
      setBusy(false);
    }
  };

  const complete = isSocioEconomicComplete(stored);

  return (
    <div className="screen min-h-[100dvh] h-full">
      <div className="screen-card" role="group" aria-labelledby="family-title">
        <div id="family-title" className="section-title">
          Data keluarga
        </div>
        <div className="subtitle">
          Data ini cukup diisi sekali. Ubah kapan saja kalau ada yang berubah.
        </div>

        {complete ? (
          <dl className="family-summary">
            <div className="family-summary-row">
              <dt>Pendidikan orang tua</dt>
              <dd>{getParentEducationLabel(stored.parentEducation)}</dd>
            </div>
            <div className="family-summary-row">
              <dt>Penghasilan keluarga</dt>
              <dd>{getFamilyIncomeLabel(stored.familyIncome)}</dd>
            </div>
            <div className="family-summary-row">
              <dt>Jenis pembiayaan</dt>
              <dd>{getFinancingTypeLabel(stored.financingType)}</dd>
            </div>
            <div className="family-summary-row">
              <dt>Nomor kontak orang tua</dt>
              <dd>{stored.parentContact}</dd>
            </div>
          </dl>
        ) : (
          <div className="subtitle" style={{ marginTop: 12 }}>
            Data keluarga belum lengkap. Isi di bawah ini ya.
          </div>
        )}

        <div className="clinical-form">
          <div className="subtitle">Ubah data</div>

          <SocioEconomicFields
            value={draft}
            onChange={setDraft}
            disabled={busy}
            idPrefix="family"
          />

          <div className="subtitle" style={{ fontSize: 11 }}>
            Boleh dikosongkan dulu dan dilengkapi nanti.
          </div>

          {error ? <div className="target-band-error">{error}</div> : null}
          {notice ? <div className="target-band-preview">{notice}</div> : null}

          <div className="target-actions">
            <button type="button" className="btn-primary" disabled={busy} onClick={handleSave}>
              {busy ? 'Menyimpan...' : 'Simpan data keluarga'}
            </button>
          </div>
        </div>

        <div className="next-actions" style={{ marginTop: 16 }}>
          <button type="button" className="btn-secondary" onClick={() => navigate('/')}>
            Kembali
          </button>
        </div>
      </div>
    </div>
  );
}
